import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Warning, Plus, PencilSimple, Trash, Bell } from "@phosphor-icons/react";
import { PRIORITIES, PRIORITY_LABEL } from "../lib/labels";

const TRIGGERS = [
  { v: "sla_response", label: "SLA prise en charge proche" },
  { v: "sla_resolution", label: "SLA résolution proche" },
  { v: "contract_overrun", label: "Contrat proche du dépassement" },
];

export default function Escalations() {
  const [items, setItems] = useState([]);
  const [techs, setTechs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [portal, setPortal] = useState({ unassigned_email_enabled: false, unassigned_email_minutes: 60, unassigned_email_exclude_list: "" });
  const [savingPortal, setSavingPortal] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const empty = {
    name: "", active: true, trigger: "sla_resolution",
    threshold_minutes: 30, threshold_percent: 90,
    raise_priority_to: "", notify_user_ids: [],
    add_comment: "L'équipe est mobilisée pour respecter les délais.",
  };
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [r, u, p] = await Promise.all([
      api.get("/escalations"),
      api.get("/users", { params: { role: "technicien" } }),
      api.get("/settings/portal"),
    ]);
    setItems(r.data);
    const admins = await api.get("/users", { params: { role: "admin" } });
    setTechs([...u.data, ...admins.data]);
    setPortal({
      unassigned_email_enabled: !!p.data?.unassigned_email_enabled,
      unassigned_email_minutes: p.data?.unassigned_email_minutes ?? 60,
      unassigned_email_exclude_list: p.data?.unassigned_email_exclude_list || "",
    });
  };

  const savePortal = async () => {
    setSavingPortal(true);
    try {
      const minutes = parseInt(portal.unassigned_email_minutes) || 60;
      // Fetch current portal payload, patch it locally, PUT the merged object
      const cur = await api.get("/settings/portal");
      const merged = {
        ...(cur.data || {}),
        unassigned_email_enabled: !!portal.unassigned_email_enabled,
        unassigned_email_minutes: Math.max(1, minutes),
        unassigned_email_exclude_list: portal.unassigned_email_exclude_list || "",
      };
      delete merged.id; delete merged.updated_at;
      await api.put("/settings/portal", merged);
      toast.success("Alerte configurée");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSavingPortal(false); }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const r = await api.get("/settings/portal/unassigned-recipients-preview");
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setPreviewLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...empty, ...r }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const body = { ...form, raise_priority_to: form.raise_priority_to || null };
    try {
      if (editing) await api.patch(`/escalations/${editing.id}`, body);
      else await api.post("/escalations", body);
      toast.success(editing ? "Règle mise à jour" : "Règle créée");
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Supprimer « ${r.name} » ?`)) return;
    await api.delete(`/escalations/${r.id}`);
    toast.success("Supprimée"); load();
  };

  const toggleActive = async (r) => {
    await api.patch(`/escalations/${r.id}`, { active: !r.active });
    load();
  };

  const toggleNotify = (uid) => {
    setForm((f) => {
      const arr = new Set(f.notify_user_ids || []);
      if (arr.has(uid)) arr.delete(uid); else arr.add(uid);
      return { ...f, notify_user_ids: Array.from(arr) };
    });
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-caps">Automatisation</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Escalades automatiques</h1>
          <p className="text-sm text-muted-foreground mt-1">Augmentez la priorité et alertez un manager dès qu'un SLA ou un contrat approche du seuil.</p>
        </div>
        <Button data-testid="new-esc-btn" onClick={openNew}><Plus size={16} className="mr-2"/> Nouvelle règle</Button>
      </header>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div>
            <div className="font-display text-lg font-semibold flex items-center gap-2">
              <Bell size={18}/> Alerte email — ticket non pris en charge
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Envoie un email à tous les techniciens + admins actifs lorsqu&apos;un ticket reste non assigné plus d&apos;un certain nombre de minutes. Une seule alerte par ticket.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Switch id="unassigned-enabled" data-testid="unassigned-enabled"
              checked={!!portal.unassigned_email_enabled}
              onCheckedChange={(v) => setPortal({...portal, unassigned_email_enabled: v})}/>
            <Label htmlFor="unassigned-enabled" className="cursor-pointer">
              Activer l&apos;alerte email
            </Label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-xl">
            <div>
              <Label>Délai (minutes)</Label>
              <Input type="number" min={1} step={1} data-testid="unassigned-minutes"
                value={portal.unassigned_email_minutes}
                onChange={(e) => setPortal({...portal, unassigned_email_minutes: e.target.value})}
                disabled={!portal.unassigned_email_enabled}/>
              <div className="text-xs text-muted-foreground mt-1">L&apos;alerte se déclenche N minutes après la création du ticket.</div>
            </div>
          </div>
          <div className="max-w-xl">
            <Label htmlFor="unassigned-exclude">Adresses à exclure du rappel</Label>
            <Textarea id="unassigned-exclude" data-testid="unassigned-exclude"
              rows={3}
              placeholder="fsoubite@netkom.fr&#10;autre-alias@netkom.fr"
              value={portal.unassigned_email_exclude_list}
              onChange={(e) => setPortal({...portal, unassigned_email_exclude_list: e.target.value})}
              disabled={!portal.unassigned_email_enabled}/>
            <div className="text-xs text-muted-foreground mt-1">
              Une adresse par ligne (ou séparées par virgules). Utile pour éviter les <b>doublons de rappel</b> quand un compte tech est redirigé vers un autre compte déjà admin (ex. <code>fsoubite@</code> forward → <code>info@</code>).
            </div>
            <div className="mt-3">
              <Button type="button" variant="outline" size="sm"
                onClick={loadPreview} disabled={previewLoading}
                data-testid="preview-recipients-btn">
                {previewLoading ? "Chargement…" : "Voir les destinataires actuels"}
              </Button>
            </div>
            {preview && (
              <div className="mt-3 rounded-lg border bg-muted/30 p-3 text-xs space-y-2" data-testid="preview-recipients-panel">
                <div className="font-medium text-sm">
                  {preview.final_count} destinataire(s) recevront le rappel :
                </div>
                {preview.final_recipients.length ? (
                  <ul className="space-y-1">
                    {preview.final_recipients.map((r) => (
                      <li key={r.email} className="flex items-center gap-2">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"/>
                        <span className="font-mono">{r.email}</span>
                        <span className="text-muted-foreground">— {r.name} ({r.role})</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-muted-foreground italic">Aucun (tous exclus)</div>
                )}
                {preview.dropped_by_exclusion_list?.length > 0 && (
                  <div className="pt-2 border-t">
                    <div className="text-muted-foreground">
                      Exclus via la liste : {preview.dropped_by_exclusion_list.map((r) => r.email).join(", ")}
                    </div>
                  </div>
                )}
                {preview.sender_mailbox && (
                  <div className="text-muted-foreground">
                    Boîte partagée <code>{preview.sender_mailbox}</code> exclue automatiquement.
                  </div>
                )}
                <div className="pt-2 border-t text-muted-foreground">
                  💡 <b>Chaque destinataire</b> reçoit 1 mail. Si vous recevez plus de mails que de destinataires listés, vérifiez vos règles de transfert Outlook/Exchange.
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button onClick={savePortal} disabled={savingPortal} data-testid="save-unassigned">
              {savingPortal ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((r) => (
          <Card key={r.id} data-testid={`esc-${r.id}`} className={`card-hover ${!r.active ? "opacity-60" : ""}`}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Warning size={18} weight="fill" className={r.active ? "text-orange-500" : "text-muted-foreground"}/>
                    <h3 className="font-display text-lg font-semibold">{r.name}</h3>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {TRIGGERS.find((t) => t.v === r.trigger)?.label || r.trigger}
                    {r.trigger === "contract_overrun"
                      ? ` — seuil ${r.threshold_percent}%`
                      : ` — ${r.threshold_minutes} min avant échéance`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[0.65rem] mono text-muted-foreground">{r.hits_count} déclench.</span>
                  <Switch checked={r.active} onCheckedChange={() => toggleActive(r)} data-testid={`esc-toggle-${r.id}`}/>
                </div>
              </div>
              <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                {r.raise_priority_to && <div>↑ Passe la priorité à <b className="text-foreground uppercase">{PRIORITY_LABEL[r.raise_priority_to] || r.raise_priority_to}</b></div>}
                {r.notify_user_ids?.length > 0 && <div><Bell size={11} className="inline mr-1"/> Notifie {r.notify_user_ids.length} personne(s)</div>}
                {r.add_comment && <div className="italic">« {r.add_comment.slice(0, 100)}{r.add_comment.length > 100 ? "…" : ""} »</div>}
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t">
                <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-esc-${r.id}`}><PencilSimple size={14}/></Button>
                <Button size="sm" variant="ghost" onClick={() => remove(r)} data-testid={`del-esc-${r.id}`}><Trash size={14}/></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="text-muted-foreground col-span-full text-center py-10">Aucune règle. Créez-en une pour ne rater aucun SLA.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Modifier la règle" : "Nouvelle règle d'escalade"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Nom</Label>
                <Input required data-testid="esc-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Ex. SLA résolution imminent"/>
              </div>
              <div className="flex items-end gap-2">
                <Switch id="esc-active" checked={!!form.active} onCheckedChange={(v) => setForm({...form, active: v})} data-testid="esc-active"/>
                <Label htmlFor="esc-active" className="mb-1">Actif</Label>
              </div>
            </div>
            <div>
              <Label>Déclencheur</Label>
              <Select value={form.trigger} onValueChange={(v) => setForm({...form, trigger: v})}>
                <SelectTrigger data-testid="esc-trigger"><SelectValue/></SelectTrigger>
                <SelectContent>{TRIGGERS.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.trigger !== "contract_overrun" ? (
              <div>
                <Label>Déclencher quand il reste ≤ (minutes)</Label>
                <Input type="number" min={1} required data-testid="esc-mins"
                  value={form.threshold_minutes} onChange={(e) => setForm({...form, threshold_minutes: parseInt(e.target.value||0,10)})}/>
              </div>
            ) : (
              <div>
                <Label>Déclencher quand la consommation ≥ (%)</Label>
                <Input type="number" min={1} max={200} required data-testid="esc-pct"
                  value={form.threshold_percent} onChange={(e) => setForm({...form, threshold_percent: parseInt(e.target.value||0,10)})}/>
              </div>
            )}
            <div>
              <Label>Augmenter la priorité à</Label>
              <Select value={form.raise_priority_to || "__none"} onValueChange={(v) => setForm({...form, raise_priority_to: v === "__none" ? "" : v})}>
                <SelectTrigger data-testid="esc-prio"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Ne pas changer</SelectItem>
                  {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABEL[p]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notifier ces personnes</Label>
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="esc-notify-list">
                {techs.map((u) => {
                  const on = form.notify_user_ids?.includes(u.id);
                  return (
                    <button
                      type="button" key={u.id}
                      onClick={() => toggleNotify(u.id)}
                      data-testid={`esc-notify-${u.id}`}
                      className={`px-2.5 py-1 rounded-full text-xs border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}
                    >
                      {u.full_name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Message ajouté en commentaire interne</Label>
              <Textarea data-testid="esc-comment" value={form.add_comment || ""} onChange={(e) => setForm({...form, add_comment: e.target.value})}/>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" data-testid="esc-save">{editing ? "Enregistrer" : "Créer"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
